import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Star, Flame, Plus, Minus, ShoppingCart, ChevronLeft, Leaf, ShieldCheck, Truck, MessageCircle, Send } from 'lucide-react';
import { fetchProduct, fetchProducts } from '../lib/api';
import { openSessionApp } from '../lib/sessionApp';
import { useCart } from '../context/CartContext';
import ProductCard from '../components/ProductCard';
import CaliLetters from '../components/CaliLetters';

const ZANGI_NUMBER = '3166750580';

const ProductDetail = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);
  const { addItem, getTieredPrice } = useCart();
  const [qty, setQty] = useState(1);
  const [activeImg, setActiveImg] = useState(null);
  const [sessionCopied, setSessionCopied] = useState(false);

  useEffect(() => {
    setLoading(true);
    setQty(1);
    setActiveImg(null);
    fetchProduct(id).then((p) => {
      setProduct(p);
      const gal = p.images && p.images.length ? p.images : [p.image];
      setActiveImg(gal[0]);
    }).catch(() => setProduct(null)).finally(() => setLoading(false));
    fetchProducts().then(setAll).catch(() => {});
  }, [id]);

  if (loading) {
    return <div className="pt-[140px] text-center min-h-[60vh] text-white/60">Ładowanie...</div>;
  }

  if (!product) {
    return (
      <div className="pt-[140px] text-center min-h-[60vh]">
        <p className="text-white/60">Nie znaleziono produktu.</p>
        <Link to="/produkty" className="btn-green inline-block mt-4 px-6 py-2">Wróć do sklepu</Link>
      </div>
    );
  }

  const related = all.filter((p) => p.id !== product.id && p.category === product.category).slice(0, 4);
  const fill = related.length < 4 ? all.filter((p) => p.id !== product.id && !related.includes(p)).slice(0, 4 - related.length) : [];
  const relatedFinal = [...related, ...fill];

  const gallery = product.images && product.images.length ? product.images : [product.image];
  const mainImg = activeImg || gallery[0];
  const unitPrice = getTieredPrice(product, qty);
  const zangiUrl = `https://services.zangi.com/dl/conversation/${ZANGI_NUMBER}`;
  const ytMatch = product.video && product.video.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([\w-]{11})/);
  const ytId = ytMatch ? ytMatch[1] : null;

  const handleSessionOrder = async () => {
    const copied = await openSessionApp();
    if (copied) {
      setSessionCopied(true);
      setTimeout(() => setSessionCopied(false), 2200);
    }
  };

  return (
    <div className="pt-[100px]">
      <div className="max-w-7xl mx-auto px-4 lg:px-8 py-8">
        <Link to="/produkty" className="inline-flex items-center gap-1 text-white/60 hover:text-[#a6e22e] text-sm mb-6"><ChevronLeft size={16} /> Wróć do produktów</Link>

        <div className="grid lg:grid-cols-2 gap-10">
          {/* Image + gallery */}
          <div>
            <div className="relative cb-panel rounded-3xl border border-white/10 overflow-hidden smoke-red">
              <img src={mainImg} alt={product.name} className={`w-full h-[420px] object-contain p-10 ${!product.inStock ? 'opacity-50 grayscale' : ''}`} />
              {product.badge && <span className="absolute top-4 left-4 bg-[#ff2e2e] text-white text-xs font-bold px-3 py-1 rounded-full">{product.badge}</span>}
              <span className="absolute top-4 right-4 bg-black/60 border border-[#a6e22e]/40 text-[#a6e22e] text-xs font-bold px-3 py-1 rounded-full inline-flex items-center gap-1"><Flame size={12} /> {product.thc}</span>
            </div>
            {gallery.length > 1 && (
              <div className="flex gap-3 mt-3">
                {gallery.map((img, k) => (
                  <button key={k} onClick={() => setActiveImg(img)} className={`h-20 w-20 rounded-xl overflow-hidden border ${mainImg === img ? 'border-[#a6e22e]' : 'border-white/10'} bg-black/40`}>
                    <img src={img} alt="" className="h-full w-full object-contain p-2" />
                  </button>
                ))}
              </div>
            )}
            {product.video && (
              <div className="mt-4 rounded-2xl overflow-hidden border border-white/10 bg-black">
                {ytId ? (
                  <iframe title="Film produktu" className="w-full aspect-video" src={`https://www.youtube.com/embed/${ytId}`} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                ) : (
                  <video src={product.video} controls className="w-full aspect-video bg-black" />
                )}
              </div>
            )}
          </div>

          {/* Info */}
          <div>
            <div className="flex items-center gap-1 text-[#ffb000] mb-3">
              {Array.from({ length: 5 }).map((_, i) => <Star key={i} size={16} className={i < Math.round(product.rating) ? 'fill-[#ffb000]' : 'opacity-30'} />)}
              <span className="text-white/50 text-sm ml-2">{product.rating} ({product.reviews} opinii)</span>
            </div>
            <h1 className="font-anton text-3xl lg:text-4xl uppercase leading-tight">
              <CaliLetters text={product.name.toUpperCase()} />
            </h1>
            <p className="text-white/50 mb-4">{product.subtitle}</p>

            <div className="flex items-end gap-3 mb-6">
              {product.oldPrice && <span className="text-white/40 line-through text-lg">{product.oldPrice.toFixed(2)} zł</span>}
              <span className="text-[#a6e22e] font-bold text-3xl">{unitPrice.toFixed(2)} zł</span>
              <span className="text-white/45 text-sm pb-1">/ szt.</span>
            </div>

            <p className="text-white/70 mb-6 leading-relaxed">{product.description}</p>

            {product.priceTiers && product.priceTiers.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-6">
                {product.priceTiers.map((tier) => (
                  <div key={`${tier.min}-${tier.max || 'plus'}`} className={`rounded-xl border px-3 py-2 text-sm ${qty >= tier.min && (tier.max === null || tier.max === undefined || qty <= tier.max) ? 'border-[#a6e22e]/70 bg-[#a6e22e]/10 text-[#a6e22e]' : 'border-white/10 bg-white/5 text-white/60'}`}>
                    <span className="block text-xs">{tier.max ? `${tier.min}-${tier.max} szt.` : `${tier.min}+ szt.`}</span>
                    <strong>{tier.price.toFixed(2)} zł</strong>
                  </div>
                ))}
              </div>
            )}

            {product.inStock ? (
              <>
                <div className="flex items-center gap-4 mb-5">
                  <div className="flex items-center gap-3 bg-white/5 rounded-full px-3 py-2 border border-white/10">
                    <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="h-7 w-7 grid place-items-center rounded-full bg-white/10 hover:bg-white/20"><Minus size={14} /></button>
                    <span className="w-8 text-center font-semibold">{qty}</span>
                    <button onClick={() => setQty((q) => q + 1)} className="h-7 w-7 grid place-items-center rounded-full bg-white/10 hover:bg-white/20"><Plus size={14} /></button>
                  </div>
                  <span className="text-[#a6e22e] text-sm">• Dostępne</span>
                </div>
                <div className="flex flex-col sm:flex-row gap-3">
                  <button onClick={() => addItem(product, qty)} className="btn-green px-7 py-3.5 inline-flex items-center justify-center gap-2 flex-1"><ShoppingCart size={18} /> Dodaj do koszyka</button>
                  <button type="button" onClick={handleSessionOrder} className="btn-ghost-red px-7 py-3.5 inline-flex items-center justify-center gap-2 flex-1"><MessageCircle size={18} /> {sessionCopied ? 'ID skopiowane' : 'Zamów na Session'}</button>
                  <a href={zangiUrl} target="_blank" rel="noreferrer" className="btn-ghost-red px-7 py-3.5 inline-flex items-center justify-center gap-2 flex-1"><Send size={18} /> Zamów na Zangi</a>
                </div>
              </>
            ) : (
              <div className="bg-white/5 border border-white/10 rounded-2xl p-5 text-center">
                <p className="font-bangers text-2xl text-white/80 mb-1">WKRÓTCE DOSTĘPNE</p>
                <p className="text-white/50 text-sm">Zapisz się do newslettera, aby nie przegapić premiery.</p>
              </div>
            )}

            <div className="grid grid-cols-3 gap-3 mt-8">
              {[{ i: ShieldCheck, t: 'Sprawdzona jakość' }, { i: Leaf, t: 'Naturalne składniki' }, { i: Truck, t: 'Dyskretna wysyłka' }].map((b, k) => (
                <div key={k} className="cb-panel rounded-xl border border-white/10 p-3 text-center">
                  <b.i className="mx-auto text-[#a6e22e] mb-1" size={20} />
                  <span className="text-white/60 text-xs">{b.t}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Related */}
        {relatedFinal.length > 0 && (
          <div className="mt-16">
            <h2 className="font-anton text-2xl lg:text-3xl uppercase mb-6">
              <CaliLetters text="MOŻE CIĘ ZAINTERESOWAĆ" />
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
              {relatedFinal.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductDetail;
